import { useNavigate } from 'react-router-dom';
import { HERO_STATS } from './data';

const FEATURES = [
  { icon: 'ri-bar-chart-fill', label: 'Mais vendas' },
  { icon: 'ri-group-fill', label: 'Times mais engajados' },
  { icon: 'ri-heart-3-fill', label: 'Clientes mais fiéis' },
  { icon: 'ri-trophy-fill', label: 'Resultados reais' },
];

export default function HeroSection() {
  const navigate = useNavigate();

  return (
    <section className="relative overflow-hidden pb-16 pt-28 md:pb-24 md:pt-36 lg:pt-40">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-[-200px] h-[760px] w-[760px] -translate-x-1/2 rounded-full bg-primary-100/70 blur-3xl"></div>
        <div className="absolute left-[-120px] top-[300px] h-[380px] w-[380px] rounded-full bg-accent-100/60 blur-3xl"></div>
        <div className="absolute right-[-120px] top-[120px] h-[520px] w-[520px] rounded-full bg-primary-100/60 blur-3xl"></div>
      </div>

      <div className="mx-auto w-full max-w-[1240px] px-6">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-[0.85fr_1.15fr] lg:gap-6 xl:grid-cols-[460px_minmax(0,1fr)]">
          <div className="max-w-xl">
            <span className="lp-fade-up inline-flex items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700">
              <i className="ri-fire-line"></i>
              Ecossistema de incentivo
            </span>

            <h2 className="lp-fade-up lp-delay-1 mt-6 font-heading text-4xl font-extrabold leading-[1.04] tracking-[-0.02em] text-foreground-950 md:text-5xl lg:text-[3.35rem]">
              Incentivo que mexe
              <br />
              o ponteiro <span className="text-primary-600">de verdade</span>
            </h2>

            <p className="lp-fade-up lp-delay-2 mt-6 max-w-lg text-base leading-relaxed text-foreground-600 md:text-lg">
              O ecossistema que conecta metas, gamificação, comunicação e premiação para engajar equipes, ativar canais e aumentar as suas vendas.
            </p>

            <div className="lp-fade-up lp-delay-3 mt-8 grid grid-cols-2 gap-x-4 gap-y-5 sm:grid-cols-4">
              {FEATURES.map((feature) => (
                <div key={feature.label} className="flex items-center gap-2.5">
                  <i className={`${feature.icon} text-xl text-primary-600`}></i>
                  <span className="text-[13px] font-semibold leading-tight text-foreground-800">{feature.label}</span>
                </div>
              ))}
            </div>

            <div className="lp-fade-up lp-delay-4 mt-9 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate('/premiado/login')}
                className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border-2 border-primary-500 px-7 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
              >
                <i className="ri-play-circle-line text-lg"></i>
                Ver demonstração
              </button>
              <button
                type="button"
                onClick={() => navigate('/solicitar-demo')}
                className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Fale com um especialista
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </div>
          </div>

          <div className="lp-fade-up lp-delay-2 relative w-full lg:max-w-none lg:-mr-6 xl:-mr-16 2xl:-mr-24">
            <img
              src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/f4100f96462d1c9a5ccc6de097f8f9f7.png"
              alt="Aplicativo GetPoints com o celular exibindo pontos e campanha Rumo ao Topo, ao lado de cards de times engajados, metas e desafios, ranking em tempo real e catálogo de prêmios"
              width={1200}
              height={860}
              className="hero-art-blend h-auto w-full select-none"
              draggable={false}
            />
          </div>
        </div>

        <div className="lp-fade-up mt-12 md:mt-16">
          <div className="flex flex-col gap-3 rounded-2xl border border-background-200/80 bg-background-100 p-3 lg:flex-row lg:items-stretch">
            <div className="grid flex-1 grid-cols-2 gap-px overflow-hidden rounded-xl bg-background-200/70 lg:grid-cols-4">
              {HERO_STATS.map((stat) => (
                <div
                  key={stat.value}
                  className="flex items-center gap-3 bg-background-50 px-4 py-4 md:px-5 md:py-5"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={`${stat.icon} text-xl`}></i>
                  </span>
                  <div className="min-w-0">
                    <p className="font-heading text-lg font-bold leading-none tracking-[-0.01em] text-foreground-950 md:text-xl">
                      {stat.value}
                    </p>
                    <p className="mt-1.5 text-xs leading-snug text-foreground-600 md:text-[13px]">{stat.label}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="relative flex w-full items-stretch overflow-hidden rounded-xl bg-background-50 lg:w-[380px]">
              <div className="relative z-10 flex flex-1 items-center gap-3 px-4 py-4">
                <button
                  type="button"
                  aria-label="Assistir como a GetPoints pode impulsionar o seu negócio"
                  className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-transform hover:scale-105"
                >
                  <i className="ri-play-fill text-xl"></i>
                </button>
                <p className="text-[13px] font-semibold leading-snug text-foreground-950">
                  Veja como a GetPoints pode impulsionar o seu negócio
                </p>
              </div>
              <div className="relative w-[42%] shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Bright%20lifestyle%20photograph%20of%20a%20smiling%20young%20woman%20holding%20a%20smartphone%20in%20a%20modern%20well-lit%20office%2C%20warm%20natural%20daylight%2C%20soft%20neutral%20beige%20background%2C%20shallow%20depth%20of%20field%2C%20professional%20editorial%20photography%2C%20high%20detail%2C%20welcoming%20and%20optimistic%20mood&width=700&height=460&seq=gp-hero-video-card-01&orientation=landscape"
                  alt="Mulher sorrindo usando o aplicativo GetPoints no celular em um escritório iluminado"
                  className="absolute inset-0 h-full w-full object-cover object-top"
                />
                <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-background-50 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}